import {
  computed, observable, makeObservable,
} from 'mobx';
import { Axios } from '../utilities/network';
import { isWeb } from '../utilities/platform';
import RootStore from './root-store';
import { GetLocalStorage, SetLocalStorage } from '../utilities/cryptography';
import { ALLERGY_OPTIONS, ALLERGY_OPTIONS_CN, CURRENT_MEDICINE_TAKEN_OPTIONS, CURRENT_MEDICINE_TAKEN_OPTIONS_CN, MODALITY, PAST_DISEASE_OPTIONS, PAST_DISEASE_OPTIONS_CN, SYMPTOM_OPTIONS, SYMPTOM_OPTIONS_CN, USER_INFO } from '../constants/storage-keys';
import { navigate } from 'gatsby';
import { CURRENT_PLATFORM } from '../constants/settings';
import { Gender } from '../constants/options';
import { ProfileTemplate } from '../utilities/profile';
import { GetLangKey, GetUserProfile, Language } from '../utilities/general';
import { FAxios } from '../utilities/file-network';

/**
 * User base information
 */
export interface UserInfo {
  id: number,
  username: string,
  firstName: string,
  lastName: string,
  email?: string,
  templateId?: ProfileTemplate | number,
  gender: Gender,
  availableClinics: any[],
  currentClinicId: number,
  isPublished: boolean,
  packagePermissions: any,
  slug?: string;
}

export enum PackageActionType {
  NONE,
  ADD,
  RENEW,
  UPGRADE,
  EXTEND,
}

export default class UserStore {
  get isAuthed() {
    return isWeb && localStorage.USER_TOKEN != null && localStorage.USER_INFO != null;
  }

  set isAuthed(value) {
    console.log('Deleting token...');
    if (!value && isWeb) localStorage.clear();
  }

  loading: boolean = false;

  loadingAccount: boolean = false;

  gettingSummary: boolean = false;

  checkingUsername: boolean = false;

  loadingBooking: boolean = false;
  loadingBookingTimeSlots: boolean = false;


  verifying: boolean = false;

  loadingOnboard: boolean = false;

  voiceToText: boolean = false;


  signingIn: boolean = false;

  userToken: string | null = null;

  versionNumber: string = "";

  translating: boolean = false;

  // openPackageWindow: boolean = false;

  defaultPackageItem: any = null;
  defaultPackageClinicId: number = 0;


  packageActionType: PackageActionType = PackageActionType.NONE;

  rootStore: RootStore;

  constructor(rootStore: RootStore) {
    makeObservable(this, {
      loading: observable,
      signingIn: observable,
      translating: observable,
      gettingSummary: observable,
      defaultPackageItem: observable,
      packageActionType: observable,
      defaultPackageClinicId: observable,
      voiceToText: observable,
      checkingUsername: observable,
      verifying: observable,
      loadingBooking: observable,
      loadingOnboard: observable,
      loadingBookingTimeSlots: observable,
      // openPackageWindow: observable,
    });
    this.rootStore = rootStore;
  }

  onOpenPackageWindow = (type: PackageActionType, defaultPackageItem: any = null, defaultPackageClinicId: number = GetUserProfile()?.currentClinicId ?? 0) => {
    // this.openPackageWindow = true;
    this.packageActionType = type;
    this.defaultPackageItem = defaultPackageItem;
    this.defaultPackageClinicId = defaultPackageClinicId;
  };

  onClosePackageWindow = () => {
    // this.openPackageWindow = false;
    this.packageActionType = PackageActionType.NONE;
    this.defaultPackageItem = null;
    this.defaultPackageClinicId = 0;
  };

  signIn = async (userModel: any) => {
    this.signingIn = true;
    try {
      const res = await Axios.post(`/api/Auth/SignIn?platformId=${CURRENT_PLATFORM}`, userModel);
      const token = `Bearer ${res.data.token}`;
      const user = res.data;
      this.userToken = user.token;
      if (isWeb) {
        localStorage.USER_TOKEN = token;
        let userInfo: UserInfo = {
          id: user.id,
          username: user.username,
          firstName: user.firstName,
          lastName: user.lastName,
          email: user.email,
          templateId: user.templateId,
          gender: user.gender,
          availableClinics: user.availableClinics,
          slug: user.slug,
          currentClinicId: user.availableClinics.length == 1 ? user.availableClinics[0].id : 0,
          isPublished: user.isPublished,
          packagePermissions: {
            features: [],
            endDate: "2077-08-20T00:00:00",
            clinicPackages: [],
          },
        };
        SetLocalStorage(USER_INFO, userInfo);
      }
      console.log("User Info", res.data);
      return Promise.resolve(res.data);
    } catch (err) {
      this.isAuthed = false;
      return Promise.reject(err);
    } finally {
      this.signingIn = false;
    }
  };

  signOut = () => {
    this.isAuthed = false;
    this.userToken = null;
    // @ts-ignore
    navigate('/');
    this.rootStore.notify('Bye', "success");
  };

  signUp = async (userModel: any, lang = GetLangKey()) => {
    this.loading = true;
    try {
      const res = await Axios.post(`/api/Auth/SignUp?lang=${lang}&platformId=${CURRENT_PLATFORM}`, userModel);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.loading = false;
    }
  };

  translateTo = async (reqModel: { targetLang: "Chinese" | "English", content: string; }) => {
    this.translating = true;
    try {
      const res = await Axios.post(`/api/Translate?platformId=${CURRENT_PLATFORM}`, reqModel);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.translating = false;
    }
  };

  //   searchModality = async (reqModel: any = {}) => {
  //     this.loading = true;
  //     try {
  //       const res = await Axios.post(`/api/Modality/Search/0/0?platformId=${CURRENT_PLATFORM}`, reqModel);
  //       SetLocalStorage(MODALITY, res.data.items);
  //       return Promise.resolve(res.data);
  //     } catch (err) {
  //       return Promise.reject(err);
  //     } finally {
  //       this.loading = false;
  //     }
  //   };

  getUserInfo = async (id: number, lang = GetLangKey()) => {
    this.loading = true;
    try {
      const res = await Axios.get(`/api/Patient?id=${id}&lang=${lang}&platformId=${CURRENT_PLATFORM}`);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.loading = false;
    }
  };

  updateUserInfo = async (reqModel: any) => {
    this.loadingAccount = true;
    try {
      const res = await Axios.put(`/api/Patient/PatientInfo?platformId=${CURRENT_PLATFORM}`, reqModel);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.loadingAccount = false;
    }
  };

  updateUserPassword = async (reqModel: any) => {
    this.loadingAccount = true;
    try {
      const res = await Axios.put(`/api/Patient/PatientMember?platformId=${CURRENT_PLATFORM}`, reqModel);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.loadingAccount = false;
    }
  };

  updateProfileImage = async (reqModel: any, lang = GetLangKey()) => {
    this.loadingAccount = true;
    try {
      const res = await Axios.put(`/api/Patient/ProfileImage?lang=${lang}&platformId=${CURRENT_PLATFORM}`, reqModel);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.loadingAccount = false;
    }
  };

  createPatientToClinic = async (reqModel: any) => {
    this.loading = true;
    try {
      const res = await Axios.post(`/api/Patient/?platformId=${CURRENT_PLATFORM}`, reqModel);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.loading = false;
    }
  };

  generatePhoneVerificationCode = async (phone: string) => {
    this.verifying = true;
    try {
      const res = await Axios.post(`/api/Contact/Phone/Generate?phone=${phone}&platformId=${CURRENT_PLATFORM}`);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.verifying = false;
    }
  };

  verifyPhoneCode = async (phone: string, code: string) => {
    this.verifying = true;
    try {
      const res = await Axios.post(`/api/Contact/Phone/Verify?phone=${phone}&code=${code}&platformId=${CURRENT_PLATFORM}`);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.verifying = false;
    }
  };

  checkOnboardInfo = async (req: any) => {
    this.loadingOnboard = true;
    try {
      const res = await Axios.post(`/api/Patient/Onboard/Check?platformId=${CURRENT_PLATFORM}`, req);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.loadingOnboard = false;
    }
  };

  searchModality = async (clinicId: number, practitionerId: number) => {
    this.loadingBooking = true;
    try {
      const res = await Axios.post('/api/Booking/Practitioner/Modality/Search/0/0', {
        note: '',
        practitionerId: practitionerId,
        clinicId
      });
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.loadingBooking = false;
    }
  };

  getTimeSlots = async (reqModel: any,) => {
    this.loadingBookingTimeSlots = true;
    try {
      const res = await Axios.post(`/api/Booking/TimeSlots?platformId=${CURRENT_PLATFORM}`, reqModel);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.loadingBookingTimeSlots = false;
    }
  };

  createBooking = async (reqModel: any) => {
    this.loadingBooking = true;
    try {
      const res = await Axios.post(`/api/Booking?platformId=${CURRENT_PLATFORM}`, reqModel);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.loadingBooking = false;
    }
  };



  // getSignUpInfoFromOrg = async (orgToken: string) => {
  // 	this.loading = true;
  // 	try {
  // 		const res = await Axios.get(`/api/Auth/Institution/SignUp?token=${orgToken}&platformId=${CURRENT_PLATFORM}`);
  // 		return Promise.resolve(res.data);
  // 	} catch (err) {
  // 		return Promise.reject(err);
  // 	} finally {
  // 		this.loading = false;
  // 	}
  // };

  getSignUpInfoFromOrgById = async (req: any) => {
    this.loading = true;
    try {
      const res = await Axios.post(`/api/Institution/Member?platformId=${CURRENT_PLATFORM}`, req);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.loading = false;
    }
  };

  checkUsername = async (req: any) => {
    this.checkingUsername = true;
    try {
      const res = await Axios.post(`/api/Auth/Check/Username/?platformId=${CURRENT_PLATFORM}`, req);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.checkingUsername = false;
    }
  };

  onboardWithBKG = async (id: any, clinicId: any) => {
    this.loadingOnboard = true;
    try {
      const res = await Axios.get(`/api/Patient/Onboard/Booking/${id}?clinicId=${clinicId}&platformId=${CURRENT_PLATFORM}`);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.loadingOnboard = false;
    }
  };

  searchOnboardPatientsByPhone = async (req: any, lang = GetLangKey()) => {
    this.loadingOnboard = true;
    try {
      const res = await Axios.post(`/api/Patient/Onboard/Check?platformId=${CURRENT_PLATFORM}&lang=${lang}`, req);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.loadingOnboard = false;
    }
  };



}
